import { InputOTP, REGEXP_ONLY_DIGITS } from '@heroui/react';
import classNames from 'classnames';
import { Fragment, useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import ErrorMessage from '@/shared/components/ErrorMessage';
import useShake from '@/shared/hooks/use-shake';

export const defaultLength = 6;

export type Props = {
  readonly name: string;
  readonly className?: string;
  readonly length?: number;
  readonly value: string[];
  readonly error?: string;
  readonly onChange: (value: string[]) => void;
};

const digitsOnly = (text: string) => text.match(/\d/g)?.join('') ?? '';

/** Slot indexes per visual group: an even code of 6+ splits in half (`123 456`). */
const getSlotGroups = (length: number) => {
  const indexes = Array.from({ length }, (_, index) => index);

  if (length < 6 || length % 2 !== 0) {
    return [indexes];
  }

  return [indexes.slice(0, length / 2), indexes.slice(length / 2)];
};

const VerificationCode = ({
  name,
  className,
  value,
  length = defaultLength,
  error,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const shakeRef = useRef<HTMLDivElement>(null);
  const errorId = useId();
  useShake(shakeRef, error);

  const code = useMemo(() => value.join('').slice(0, length), [length, value]);
  const slotGroups = useMemo(() => getSlotGroups(length), [length]);

  const hasMounted = useRef(false);

  // Callers reset to `[]` after a rejected code; put the caret back for the retry. The first run
  // is the mount, where scrolling to the field would push the page's title and steps off screen.
  useEffect(() => {
    if (value.length === 0) {
      inputRef.current?.focus(hasMounted.current ? undefined : { preventScroll: true });
    }
    // eslint-disable-next-line @silverhand/fp/no-mutation
    hasMounted.current = true;
  }, [value]);

  return (
    <div className={classNames('flex flex-col gap-2', className)}>
      {/* Codes are digit strings, which fill left to right in every locale. */}
      <div ref={shakeRef} dir="ltr" className="t-input">
        <InputOTP
          ref={inputRef}
          name={name}
          maxLength={length}
          value={code}
          pattern={REGEXP_ONLY_DIGITS}
          inputMode="numeric"
          autoComplete="one-time-code"
          pasteTransformer={digitsOnly}
          // The slots already span the full width, so there is no room to reserve for a
          // password-manager badge.
          pushPasswordManagerStrategy="none"
          isInvalid={Boolean(error)}
          aria-label={t('input.code')}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(next) => {
            onChange(next.split(''));
          }}
        >
          {slotGroups.map((group, groupIndex) => (
            <Fragment key={group[0]}>
              {/* The default separator colour nearly matches the page in dark mode. */}
              {groupIndex > 0 && <InputOTP.Separator className="bg-border" />}
              <InputOTP.Group className="flex-1">
                {group.map((index) => (
                  <InputOTP.Slot key={index} index={index} className="h-12 tabular-nums" />
                ))}
              </InputOTP.Group>
            </Fragment>
          ))}
        </InputOTP>
      </div>
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </div>
  );
};

export default VerificationCode;
